/**
 * The Skeleton3D scene pass. A MeshInstance3D that names a Skeleton3D through `skeleton` binds to
 * the automatic skin of that skeleton, or to the Skin it sets through `skin`
 * (`MeshInstance3D::_resolve_skeleton_path`, `mesh_instance_3d.cpp:175-199`). The component cannot
 * reach the skeleton, so this pass poses each skeleton once and hands the mesh its skin by its live
 * path. The component reads the Skin resource and asks the skin for it with `withSkin`. It runs on
 * the live stage (ADR-0060), as Godot resolves the path on the live tree, across an instance border too. The SkeletonModifier3D
 * children of the skeleton edit the pose first, in child order, and so do they on the pose that an
 * AnimationPlayer writes to a bone. A BoneAttachment3D seat reads the same posed skeleton, so no
 * reader poses the skeleton again in its own way.
 */
import { identityTransform3D, sameTransform3D } from '../../../godot/math/transform3d.js';
import { resolveNodePathLiteral } from '../../../godot/string/nodePath.js';
import { nodeComponentRegistry } from '../../../r3f/NodeComponentRegistry.js';
import { liveGlobalTransform3D } from '../../../r3f/liveGlobalTransform.js';
import { uniquePathsOfLiteral } from '../../../r3f/nodePathUniqueNames.js';
import { liveNodePatches } from '../../../r3f/liveNodePatches.js';
import { liveScenePassResults } from '../../../r3f/liveScenePass.js';
import { sameElements } from '../../../utils/sameElements.js';
import { perObject } from '../../../utils/perObject.js';
import { warnOnce } from '../../../utils/warnOnce.js';
import { animationPass, automaticSkinMatrices, BonePoses, poseUnder, } from './skeletonPose.js';
/**
 * The skin of every MeshInstance3D in `tree` bound to a Skeleton3D, by the mesh's live path. A node
 * that writes no `skeleton` binds nothing, as Godot's default is empty unless the project turns on
 * `default_parent_skeleton_in_mesh_instance_3d` (`mesh_instance_3d.cpp:937`). A path that is empty,
 * absolute, resolves to no node, or names a node that is not a Skeleton3D binds nothing.
 */
export function bindMeshesToSkeletons(tree) {
    const skins = new Map();
    tree.fold(undefined, ({ step, path }, _, descend) => {
        const skin = boundSkinOf(step.node, path, tree);
        if (skin)
            skins.set(path, skin);
        descend();
    });
    return skins;
}
/** Whether a MeshInstance3D with `properties` can bind a skeleton: it names one. */
export function namesSkeletonToBind(properties) {
    return properties.skeleton !== undefined;
}
/** The skin of the node at `path` where it is a MeshInstance3D bound to a Skeleton3D, else undefined. */
function boundSkinOf(node, path, tree) {
    if (node.type !== 'MeshInstance3D')
        return undefined;
    const properties = node.properties;
    if (!namesSkeletonToBind(properties))
        return undefined;
    const skeletonPath = resolveNodePathLiteral(path, properties.skeleton, uniquePathsOfLiteral(properties.skeleton, path, tree));
    const skeleton = skeletonPath === null ? null : tree.at(skeletonPath);
    if (skeleton?.step.node.type !== 'Skeleton3D')
        return undefined;
    return posedSkeletonOf(skeleton, tree).skin;
}
function modifiersOf(skeleton, tree, placed) {
    const modifiers = { edits: [], nodes: [], skeletonGlobal: null, reads: [] };
    tree.fold(undefined, ({ step, path: modifierPath }) => {
        const reader = nodeComponentRegistry.poseModifierOf(step.node.type);
        if (!reader)
            return;
        modifiers.skeletonGlobal ??=
            liveGlobalTransform3D(tree, skeleton.path, placed) ?? identityTransform3D();
        const edit = reader(step.node, {
            skeletonGlobal: modifiers.skeletonGlobal,
            nodeGlobal: (target) => {
                const earlier = modifiers.reads.find((read) => read.modifierPath === modifierPath && read.target === target);
                if (earlier)
                    return earlier.global;
                const global = referenceNodeGlobal(tree, placed, modifierPath, target);
                modifiers.reads.push({ modifierPath, target, global });
                return global;
            },
        });
        modifiers.nodes.push(step.node);
        if (edit)
            modifiers.edits.push(edit);
    }, skeleton);
    return modifiers;
}
/** Whether the scene `tree` holds answers each read of `previous` as it did, so its edits would make the same pose. */
function readsHold(previous, current, tree, placed) {
    return (sameWorld(previous.skeletonGlobal, current.skeletonGlobal) &&
        previous.reads.every((read) => sameWorld(read.global, referenceNodeGlobal(tree, placed, read.modifierPath, read.target))));
}
/** The global transform of the Node3D that `target` names from the modifier at `modifierPath`. */
function referenceNodeGlobal(tree, placed, modifierPath, target) {
    const resolved = resolveNodePathLiteral(modifierPath, target, uniquePathsOfLiteral(target, modifierPath, tree));
    return resolved === null ? null : liveGlobalTransform3D(tree, resolved, placed);
}
function sameWorld(a, b) {
    return a === b || (a !== null && b !== null && sameTransform3D(a, b));
}
/**
 * One skin for each Skeleton3D node with no modifier child, shared by every mesh bound to it. A
 * load that leaves the node as it is hands back the same skin, so a bound mesh does not draw again.
 */
const automaticSkinOf = perObject((skeleton) => {
    const { bones, showRestOnly } = skeleton.properties;
    return automaticSkinMatrices(bones, showRestOnly);
});
/**
 * Written only by `posedSkeletonOf`: the previous posed skeleton of each Skeleton3D node at each
 * live path. A modifier reads the scene around the skeleton, so two instances of one node pose
 * differently. The entries clear when nothing holds the node any more.
 */
const keptPoses = perObject(() => new Map());
/**
 * The posed skeleton of the Skeleton3D at `skeleton`: the file pose, edited by the modifiers below
 * it, which the skin of a bound mesh and a BoneAttachment3D seat both read. A load that leaves the
 * node, the modifier nodes and what they read as they were hands back the same posed skeleton, over
 * which the skin runs the poses it holds.
 */
export function posedSkeletonOf(skeleton, tree) {
    // Where the nodes stand once a RemoteTransform has moved them, as a modifier reads them.
    const placed = liveScenePassResults(liveNodePatches, tree);
    const modifiers = modifiersOf(skeleton, tree, placed);
    const kept = keptPoses(skeleton.step.node);
    const previous = kept.get(skeleton.path);
    if (previous &&
        sameElements(previous.modifiers.nodes, modifiers.nodes) &&
        readsHold(previous.modifiers, modifiers, tree, placed)) {
        return previous.posed;
    }
    const posed = makePosed(skeleton, modifiers);
    kept.set(skeleton.path, { modifiers, posed });
    return posed;
}
function makePosed(skeleton, modifiers) {
    const { bones, showRestOnly, motionScale } = skeleton.step.node.properties;
    // One pose editor for the skeleton: each frame starts it again from the file pose.
    const poses = new BonePoses(bones, showRestOnly);
    const warnUnknownBone = (name) => warnOnce(poses, name, `[AnimationPlayer] the Skeleton3D at ${skeleton.path} has no bone "${name}", so its track binds nothing`);
    // The pose `poses` holds once the edits of one clip pose ran on it, null for the file pose. Every
    // skin of the skeleton shares it, so the edits run once for each pose however many skins ask.
    let preparedFor;
    const prepare = (pose) => {
        if (pose !== preparedFor) {
            preparedFor = pose;
            poseUnder(poses, pose ? [animationPass(pose, motionScale, warnUnknownBone), ...modifiers.edits] : modifiers.edits);
        }
        return poses;
    };
    return {
        findBone: (name) => poses.findBone(name),
        globalPose: (bone) => prepare(null).globalPose(bone),
        globalPoseUnder: (bone, pose) => prepare(pose).globalPose(bone),
        skin: makeSkin(skeleton, bones, showRestOnly, poses, prepare, modifiers.edits),
    };
}
/** The automatic skin over `poses`, from the file pose and under each pose a clip writes. */
function makeSkin(skeleton, bones, showRestOnly, poses, prepare, edits) {
    /** The matrices of `bindings` (the automatic ones where undefined) under each pose, kept for the last. */
    const matricesUnderOf = (bindings) => {
        let lastPose;
        let lastMatrices = [];
        return (pose) => {
            if (pose === lastPose)
                return lastMatrices;
            lastPose = pose;
            const posed = prepare(pose);
            lastMatrices = bindings ? posed.bindMatrices(bindings) : posed.skinMatrices();
            return lastMatrices;
        };
    };
    /** One skin for each decoded Skin, shared by every mesh that sets it. */
    const skinsOfBinds = new WeakMap();
    const skinOfBinds = (binds) => {
        const kept = skinsOfBinds.get(binds);
        if (kept)
            return kept;
        const bindings = poses.resolveBinds(binds, (message) => warnOnce(binds, message, `[MeshInstance3D] the Skeleton3D at ${skeleton.path}: ${message}`));
        const bound = {
            skeletonPath: skeleton.path,
            matrices: prepare(null).bindMatrices(bindings),
            matricesUnder: matricesUnderOf(bindings),
        };
        skinsOfBinds.set(binds, bound);
        return bound;
    };
    return {
        skeletonPath: skeleton.path,
        matrices: edits.length === 0
            ? automaticSkinOf(skeleton.step.node)
            : automaticSkinMatrices(bones, showRestOnly, edits),
        matricesUnder: matricesUnderOf(),
        withSkin: skinOfBinds,
    };
}
//# sourceMappingURL=skeletonBindings.js.map